// src/components/TrySomethingNew.jsx
// ============================================================
// "I wonder what people get out of birdwatching"
// — JEzza, thinking about his dad
//
// This component surfaces events OUTSIDE the user's usual
// interests. Not pushy. Just a gentle "hey, have you thought
// about this?" Like a mate who says "you should come along."
// ============================================================

import React, { useState, useEffect } from 'react';
import {
  CaretRight,
  Sparkle,
  X
} from '@phosphor-icons/react';
import { API_BASE as API_URL } from '../apiConfig';

const ACCENT = '#2dd4bf';

const NUDGES = [
  "Ever wondered what people get out of this?",
  "Something different. No commitment — just come see.",
  "You might surprise yourself.",
  "New experiences, new people. Worth a try?",
  "Step outside the usual. Just this once.",
  "Your future self might thank you for this one.",
];

export default function TrySomethingNew({ token, userInterests = [], onSelectEvent }) {
  const [events, setEvents] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [nudge] = useState(() => NUDGES[Math.floor(Math.random() * NUDGES.length)]);

  useEffect(() => {
    if (!token || dismissed) return;

    fetch(`${API_URL}/api/connect/events/discover?limit=20`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (!data.success) return;
        // Filter to events OUTSIDE the user's interests
        const userCats = new Set((userInterests || []).map(i => i.toLowerCase()));
        const outside = (data.data?.events || []).filter(e => {
          const cat = (e.category || '').toLowerCase();
          return !userCats.has(cat) && !Array.from(userCats).some(c => cat.includes(c));
        });
        // Pick up to 3 random ones
        const shuffled = outside.sort(() => 0.5 - Math.random());
        setEvents(shuffled.slice(0, 3));
      })
      .catch(() => {});
  }, [token, userInterests, dismissed]);

  if (dismissed || events.length === 0) return null;

  return (
    <div style={{
      background: 'rgba(45,212,191,0.04)',
      border: '1px solid rgba(45,212,191,0.12)',
      borderRadius: 16,
      padding: '16px 18px',
      marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkle size={15} color={ACCENT} />
          <span style={{ fontSize: 14, fontWeight: 700, color: '#e4e4e7' }}>Try something new</span>
        </div>
        <button onClick={() => setDismissed(true)}
          style={{ background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', padding: 2 }}>
          <X size={14} />
        </button>
      </div>

      <p style={{ fontSize: 12, color: '#a1a1aa', margin: '0 0 14px', lineHeight: 1.5, fontStyle: 'italic' }}>
        {nudge}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {events.map(event => (
          <div key={event._id}
            onClick={() => onSelectEvent?.(event)}
            role="button"
            tabIndex={0}
            aria-label={`${event.title} — ${event.category}`}
            onKeyDown={e => e.key === 'Enter' && onSelectEvent?.(event)}
            style={{
            display: 'flex', alignItems: 'center', gap: 12,
            background: '#111113', borderRadius: 12, padding: '12px 14px',
            border: '1px solid #1a1a1e', cursor: 'pointer',
            transition: 'border-color 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#2a2a2e'}
          onMouseLeave={e => e.currentTarget.style.borderColor = '#1a1a1e'}
          >
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'rgba(45,212,191,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, flexShrink: 0,
            }}>
              {getCategoryEmoji(event.category)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e4e4e7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {event.title}
              </div>
              <div style={{ fontSize: 11, color: '#71717a' }}>
                {event.category} · {formatShortDate(event.startDate)}
              </div>
            </div>
            <CaretRight size={14} color="#52525b" />
          </div>
        ))}
      </div>
    </div>
  );
}

function getCategoryEmoji(cat) {
  const map = {
    'Sports & Fitness': '⚽', 'Music & Concerts': '🎵', 'Food & Drink': '🍕',
    'Arts & Creative': '🎨', 'Outdoors & Nature': '🌿', 'Social & Meetups': '☕',
    'Technology': '💻', 'Learning': '📚', 'Wellness': '🧘',
    'Community': '🤝', 'Family': '👨‍👩‍👧',
  };
  return map[cat] || '✨';
}

function formatShortDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = d - now;
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days < 7) return d.toLocaleDateString('en-AU', { weekday: 'long' });
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}
